import { useState } from 'react';
import { Btn } from './ui/Btn.jsx';
import { CaseTauxVerifiee } from './CaseTauxVerifiee.jsx';
import { Inp, _inp } from './ui/Inputs.jsx';
import { CANTONS, cantonDuNpa } from '../lib/canton';
import {
  ANNEE_BAREME,
  anneeApplicable,
  anneeCourante,
  anneesReglees,
  avecBaremeParDefaut,
  avecLigneDuPays,
  avecTauxDeLAnnee,
  lignesReglees,
  origineDesTaux,
  preparerAnnee,
  tauxCharges,
  tauxDefautDeLaSociete,
  tauxModifies,
} from '../lib/charges';
import { MODES_13, mode13 } from '../lib/salaire13';
import { C } from '../theme/palette';
import { decimalesDuPays } from '../lib/currency';

/**
 * Une case de taux qui accepte la virgule (« 9,5 » en Allemagne) : la case
 * numérique la refusait, et montrait « 9.3 » quand le pays écrit « 9,3 ».
 * Le taux part dès qu'il se lit comme un nombre.
 */
function CaseTaux({ valeur, onValide, ...reste }) {
  const [texte, setTexte] = useState(null);
  const sep = decimalesDuPays();
  return (
    <input
      {...reste}
      type="text"
      inputMode="decimal"
      value={texte ?? (valeur === '' || valeur == null ? '' : String(valeur).replace('.', sep))}
      onChange={(e) => {
        const v = e.target.value;
        setTexte(v);
        const n = v.replace(',', '.').trim();
        if (/^\d*\.?\d*$/.test(n)) onValide(n);
      }}
      onBlur={() => setTexte(null)}
    />
  );
}

/**
 * Éditeur des charges sociales de l'entreprise, année par année.
 *
 * <p>Le même tableau s'écrivait trois fois : sur la liste de l'équipe, dans la
 * fiche de salaire d'un employé, et dans les réglages. Trois copies du même
 * formulaire, que rien n'obligeait à rester d'accord — et de fait elles ne
 * l'étaient déjà plus. Il ne vit plus qu'ici, dans les réglages ; les autres
 * écrans lisent les mêmes taux et renvoient ici pour les modifier.
 *
 * <p>Les taux changent chaque année. L'écran propose l'année en cours, laisse
 * préparer la suivante en copiant les taux en vigueur, et garde les années
 * passées consultables : les fiches déjà émises sont figées, mais le certificat
 * et la déclaration d'une année passée se recalculent avec les taux d'alors.
 *
 * @param {object} props propriétés du composant
 * @param {object} [props.soc] société en cours d'édition
 * @param {Function} [props.setSoc] enregistre la société modifiée
 * @param {string} [props.note] phrase de fin, propre à l'écran qui l'affiche
 * @returns {import('react').ReactElement} le formulaire
 */
export function EditeurCharges({ soc, setSoc, note }) {
  const courante = anneeCourante();
  const [annee, setAnnee] = useState(courante);
  const annees = [...new Set([...anneesReglees(soc), courante])].sort((a, b) => a - b);
  // Préparer plus d'une année à l'avance n'a pas de sens : les caisses ne
  // publient leurs taux qu'à l'automne pour l'année suivante.
  const suivante = annees[annees.length - 1] + 1;
  const peutPreparer = suivante <= courante + 1;
  const source = anneeApplicable(soc, annee);

  const T = tauxCharges(soc, annee);
  const setT = (cle, valeur) =>
    setSoc && setSoc((st) => avecTauxDeLAnnee(st, annee, tauxModifies(st, annee, cle, valeur)));
  const perso = (soc && soc.chargesPerso) || [];
  const setPerso = (fn) =>
    setSoc && setSoc((st) => ({ ...st, chargesPerso: fn((st && st.chargesPerso) || []) }));
  const preparer = () => {
    setSoc && setSoc((st) => preparerAnnee(st, suivante));
    setAnnee(suivante);
  };

  const titre = (texte, couleur) => (
    <div
      style={{
        fontSize: 8.5,
        fontWeight: 800,
        color: couleur,
        textTransform: 'uppercase',
        marginBottom: 5,
      }}
    >
      {texte}
    </div>
  );

  // Le barème par défaut de la société (son canton), pour dire le taux normal
  // sous chaque case et y revenir d'un geste.
  const D = tauxDefautDeLaSociete(soc);
  const R = (libelle, cle) => (
    <div style={{ flex: 1, marginBottom: 8 }}>
      <CaseTauxVerifiee
        libelle={libelle}
        valeur={T[cle]}
        defaut={D[cle]}
        inhabituel={+D[cle] > 0 ? Math.max(+D[cle] * 3, +D[cle] + 3) : 15}
        onChange={(n) => setT(cle, n)}
        onRemettre={() => setT(cle, D[cle])}
        pourcent={false}
      />
    </div>
  );

  const etatAnnee = () => {
    if (source === annee) {
      return annee < courante
        ? 'Année passée : ses fiches déjà émises ne changent pas, mais le certificat et la déclaration de cette année se recalculent avec ces taux.'
        : `Taux réglés pour ${annee}.`;
    }
    if (source == null && !suisse)
      return `Rien n'est réglé pour ${annee} : les taux du pays s'appliquent. Modifie une ligne pour fixer ceux de cette année.`;
    return source == null
      ? `Rien n'est réglé pour ${annee} : le barème ${ANNEE_BAREME} s'applique. Modifie une ligne pour fixer les taux de cette année.`
      : `Rien n'est réglé pour ${annee} : les taux de ${source} restent en vigueur. Modifie une ligne pour fixer ceux de ${annee}.`;
  };

  const origine = origineDesTaux(soc);
  const suisse = origine.pays === 'CH';
  // Hors de Suisse, chaque cotisation du pays a sa ligne, part de l'employeur
  // et part de l'employé, comme sur la fiche de paie ; un seul taux global ne
  // reste que pour un pays sans lignes connues.
  const lignes = suisse ? [] : lignesReglees(soc, annee);
  const parLigne = lignes.length > 0;
  const setLigne = (nom, part, valeur) =>
    setSoc && setSoc((st) => avecLigneDuPays(st, annee, nom, part, valeur));
  const totalLignes = (part) => Math.round(lignes.reduce((a, l) => a + l[part], 0) * 1000) / 1000;
  const cantonNpa = cantonDuNpa(soc?.npa);
  const detail = (liste) =>
    liste.length ? (
      <div style={{ fontSize: 8.5, color: C.muted, margin: '-4px 0 9px', lineHeight: 1.5 }}>
        {liste.map((c) => c.lib + ' ' + c.taux + ' %').join(' · ')}
      </div>
    ) : null;

  return (
    <div>
      {/* Le canton fixe les allocations familiales et les cotisations
          cantonales ; il se lit sur le code postal, et se change ici. */}
      {suisse ? (
        <div style={{ marginBottom: 12 }}>
          {titre('Canton de la société', C.navy)}
          <select
            aria-label="Canton de la société"
            value={origine.canton || ''}
            onChange={(e) =>
              setSoc &&
              // Le canton choisi s'enregistre tel quel, celui du code postal
              // compris : remis à vide, il disparaissait du sac de réglages
              // sans que rien parte au serveur (seules les clés envoyées se
              // comparent), et l'ancien canton revenait au rechargement.
              setSoc((st) => ({ ...st, canton: e.target.value || '' }))
            }
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '10px 11px',
              fontSize: 11,
              background: '#fff',
            }}
          >
            {!origine.canton && <option value="">—</option>}
            {Object.entries(CANTONS).map(([code, nom]) => (
              <option key={code} value={code}>
                {nom}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 8.5, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
            {cantonNpa && origine.canton === cantonNpa
              ? 'Trouvé avec le code postal de la société.'
              : 'Choisi à la main.'}
          </div>
        </div>
      ) : (
        <div
          style={{
            fontSize: 9.5,
            color: C.ink,
            background: C.amberL,
            borderRadius: 9,
            padding: '9px 11px',
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {parLigne
            ? 'Hors de Suisse : les cotisations obligatoires du pays, ligne par ligne, part de l’employeur et part de l’employé. Chaque taux se corrige ci-dessous ; vérifie-les avec ta fiduciaire.'
            : origine.connu
              ? 'Hors de Suisse : charges sociales moyennes du pays, à titre d’estimation. Vérifie-les avec ta fiduciaire.'
              : 'Hors de Suisse : aucune charge par défaut pour ce pays. Règle-les ci-dessous.'}
          {origine.note ? <div style={{ color: C.muted, marginTop: 4 }}>{origine.note}</div> : null}
        </div>
      )}

      {titre('Année de paie', C.navy)}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {annees.map((a) => {
          const actif = a === annee;
          return (
            <button
              key={a}
              type="button"
              aria-pressed={actif}
              onClick={() => setAnnee(a)}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                fontSize: 10.5,
                fontWeight: 700,
                cursor: 'pointer',
                border: '1.5px solid ' + (actif ? C.navy : C.border),
                background: actif ? C.navy : '#fff',
                color: actif ? '#fff' : C.ink,
              }}
            >
              {a}
            </button>
          );
        })}
        {peutPreparer && (
          <Btn
            label={`➕ Préparer l'année ${suivante}`}
            sm
            ghost
            color={C.navy}
            onClick={preparer}
          />
        )}
      </div>
      <div style={{ fontSize: 8.5, color: C.muted, margin: '7px 0 11px', lineHeight: 1.5 }}>
        {etatAnnee()}
      </div>

      {parLigne && (
        <div style={{ marginBottom: 12 }}>
          {titre(`Cotisations du pays ${annee} (%)`, C.amber)}
          <div
            style={{
              display: 'flex',
              gap: 7,
              fontSize: 8.5,
              fontWeight: 700,
              color: C.muted,
              marginBottom: 4,
            }}
          >
            <div style={{ flex: 2 }}>Cotisation</div>
            <div style={{ flex: 1 }}>Employeur</div>
            <div style={{ flex: 1 }}>Employé</div>
          </div>
          {lignes.map((l) => (
            <div
              key={l.libelleFr}
              data-ligne-pays={l.libelleFr}
              style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 6 }}
            >
              <div style={{ flex: 2, fontSize: 10, color: C.ink, lineHeight: 1.35, minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{l.libelle}</div>
                {l.libelle !== l.libelleFr && (
                  <div style={{ fontSize: 8.5, color: C.muted }}>{l.libelleFr}</div>
                )}
                {l.base && <div style={{ fontSize: 8, color: C.muted }}>{l.base}</div>}
              </div>
              <div style={{ flex: 1 }}>
                <CaseTaux
                  aria-label={`${l.libelleFr} — part de l’employeur`}
                  valeur={l.pat}
                  onValide={(v) => setLigne(l.libelleFr, 'pat', v)}
                  style={{ ..._inp, width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <CaseTaux
                  aria-label={`${l.libelleFr} — part de l’employé`}
                  valeur={l.sal}
                  onValide={(v) => setLigne(l.libelleFr, 'sal', v)}
                  style={{ ..._inp, width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              gap: 7,
              fontSize: 10,
              fontWeight: 800,
              color: C.navy,
              borderTop: '1px solid ' + C.border,
              paddingTop: 6,
            }}
          >
            <div style={{ flex: 2 }}>Total</div>
            <div style={{ flex: 1 }}>{totalLignes('pat')} %</div>
            <div style={{ flex: 1 }}>{totalLignes('sal')} %</div>
          </div>
        </div>
      )}

      {!parLigne && titre(`Charges patronales ${annee} (%)`, C.amber)}
      {suisse && (
        <>
          <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
            {R('AVS/AI/APG', 'avsPat')}
            {R('Chômage AC', 'acPat')}
            {R('Accident AAP', 'aapPat')}
          </div>
          <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
            {R('Alloc. fam.', 'afPat')}
            {R('Frais admin', 'adminPat')}
            {R('IJM maladie', 'ijmPat')}
          </div>
        </>
      )}
      {!parLigne && (
        <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
          {R(suisse ? 'Cotisations du canton' : 'Charges du pays (estimation)', 'cantonPat')}
        </div>
      )}
      {detail(origine.detailPat)}

      {!parLigne && titre(`Déductions salarié ${annee} (%)`, C.red)}
      {suisse && (
        <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
          {R('AVS/AI/APG', 'avsSal')}
          {R('Chômage AC', 'acSal')}
          {R('Accident AANP', 'aanpSal')}
          {R('IJM maladie', 'ijmSal')}
        </div>
      )}
      {!parLigne && (
        <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
          {R(suisse ? 'Cotisations du canton' : 'Charges du pays (estimation)', 'cantonSal')}
        </div>
      )}
      {detail(origine.detailSal)}
      <div style={{ marginBottom: 3 }} />

      {titre('Charges personnalisées', C.amber)}
      {perso.map((c) => (
        <div
          key={c.id}
          style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 7 }}
        >
          <div style={{ flex: 2 }}>
            <Inp
              label="Libellé"
              value={c.label}
              onChange={(e) =>
                setPerso((l) => l.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)))
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <Inp
              label="%"
              type="number"
              value={c.pct}
              onChange={(e) =>
                setPerso((l) =>
                  l.map((x) =>
                    x.id === c.id ? { ...x, pct: e.target.value === '' ? 0 : +e.target.value } : x,
                  ),
                )
              }
            />
          </div>
          <Btn
            label="🗑"
            sm
            ghost
            color={C.red}
            onClick={() => setPerso((l) => l.filter((x) => x.id !== c.id))}
          />
        </div>
      ))}
      <div style={{ marginBottom: 9 }}>
        <Btn
          label="➕ Ajouter une charge patronale"
          sm
          ghost
          color={C.navy}
          full
          onClick={() =>
            setPerso((l) => [...l, { id: Date.now(), label: 'Nouvelle charge', pct: 0 }])
          }
        />
      </div>
      <Btn
        label={`↺ Remettre le barème par défaut pour ${annee}`}
        sm
        ghost
        color={C.grey}
        full
        onClick={() => setSoc && setSoc((st) => avecBaremeParDefaut(st, annee))}
      />
      <div style={{ fontSize: 8.5, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
        {/* L'annee du barème accompagne la note où qu'elle s'affiche : c'est
            elle qui vieillit, et une note qui ne la porte pas laisse croire que
            les taux sont ceux de cette année. */}
        {suisse ? (
          <>
            Barème {ANNEE_BAREME} par défaut, indicatif — chaque janvier, vérifie les taux auprès de
            ta caisse de compensation, de ton assureur LAA et de ton institution LPP, puis règle-les
            ici pour la nouvelle année.{' '}
            {note ||
              "Ces taux s'appliquent à toute l'équipe ; le LPP et l'impôt à la source se règlent par employé, dans sa fiche."}
          </>
        ) : (
          'Taux du pays par défaut, indicatifs — chaque janvier, vérifie-les auprès des caisses sociales du pays ou de ta fiduciaire, puis règle-les ici pour la nouvelle année. Ces taux s’appliquent à toute l’équipe.'
        )}
      </div>
      {/* Absent de la maquette : après sa note, pour ne rien en déplacer. */}
      <div style={{ marginTop: 13 }}>
        {titre('Versement du 13e salaire', C.green)}
        <select
          value={mode13(soc)}
          onChange={(e) => setSoc && setSoc((st) => ({ ...st, mode13: e.target.value }))}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '10px 11px',
            fontSize: 11,
            background: '#fff',
            marginBottom: 7,
          }}
        >
          {Object.entries(MODES_13).map(([cle, m]) => (
            <option key={cle} value={cle}>
              {m.libelle}
            </option>
          ))}
        </select>
        <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.5 }}>
          Le 13e est un élément de salaire ordinaire : il supporte les mêmes charges que le reste.
          Une année incomplète donne droit à la part des mois travaillés, le mois entamé comptant
          pour un mois entier.
        </div>
      </div>
    </div>
  );
}
